// =============================================================================
// Session timeline helpers
// Reusable utilities for polling devops-orchestrator-sessions (DynamoDB)
// and resolving approval context across any agent flow.
//
// To add a new agent type:
//   1. Add an entry to SESSION_TYPE_REGISTRY below.
//   2. Use its prefix when generating the sessionId (e.g. `ui-gitlab-<uuid>`).
//   No component changes required.
// =============================================================================

import { graphqlQuery } from './graphqlApi';
import { getSessionTimeline } from '../graphql/queries';

// ---------------------------------------------------------------------------
// Session type registry
// Each entry describes a prefix pattern and the human-readable messages
// shown in the UI when a decision is made.
// ---------------------------------------------------------------------------
const SESSION_TYPE_REGISTRY = [
  {
    prefix: 'ui-plan-',
    label: 'DevOps Plan',
    allowMessage: 'User has allowed the plan. Creating Jira Ticket...',
    discardMessage: 'Plan rejected.',
  },
  {
    prefix: 'ui-gitlab-',
    label: 'GitLab Code Review',
    allowMessage: 'User has allowed the GitLab code review.',
    discardMessage: 'Code review rejected.',
  },
  {
    prefix: 'ui-service-need-',
    label: 'Service Need',
    allowMessage: 'User has allowed the service need.',
    discardMessage: 'Service need rejected.',
  },
];

const DEFAULT_SESSION_TYPE = {
  label: 'Agent',
  allowMessage: 'User has allowed the plan.',
  discardMessage: 'Plan rejected.',
};

/**
 * Resolves the session type config for a given sessionId.
 *
 * @param {string} [sessionId='']
 * @returns {{ prefix?: string, label: string, allowMessage: string, discardMessage: string }}
 */
export const resolveSessionType = (sessionId = '') =>
  SESSION_TYPE_REGISTRY.find((type) => sessionId.startsWith(type.prefix)) ?? DEFAULT_SESSION_TYPE;

/**
 * Returns the human-readable message for an approval decision.
 *
 * @param {'ALLOW'|'DISCARD'} decision
 * @param {string} [sessionId='']
 * @returns {string}
 */
export const getApprovalMessage = (decision, sessionId = '') => {
  const type = resolveSessionType(sessionId);
  return decision === 'ALLOW' ? type.allowMessage : type.discardMessage;
};

/**
 * Safely parse the AWSJSON `data` field from a session entry.
 * Handles double-encoded JSON strings.
 *
 * @param {string|object} raw
 * @returns {object|string|null}
 */
export const parseEntryData = (raw) => {
  if (!raw) return null;
  let parsed = raw;
  for (let i = 0; i < 3 && typeof parsed === 'string'; i += 1) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return parsed;
    }
  }
  return parsed;
};

/**
 * Checks if a session has an APPROVAL_RESPONSE entry with a given decision.
 * Works for any agent that writes APPROVAL_RESPONSE entries to the timeline.
 *
 * @param {string} sessionId
 * @param {'ALLOW'|'DISCARD'} [expectedDecision='ALLOW']
 * @returns {Promise<{ found: boolean, entry: object|null, decision: string|null }>}
 */
export const checkApprovalDecision = async (sessionId, expectedDecision = 'ALLOW') => {
  if (!sessionId) return { found: false, entry: null, decision: null };

  try {
    const res = await graphqlQuery(getSessionTimeline, {
      sessionId,
      entryType: 'APPROVAL_RESPONSE',
    });
    const items = (res?.getSessionTimeline?.items || []).filter(
      (entry) => entry?.entryType === 'APPROVAL_RESPONSE'
    );

    for (const item of items) {
      const data = parseEntryData(item?.data);
      const decision = data?.decision ?? null;
      if (decision === expectedDecision) {
        return { found: true, entry: item, decision };
      }
    }

    return { found: false, entry: null, decision: null };
  } catch (err) {
    console.warn('[sessionHelpers] checkApprovalDecision error:', err.message);
    return { found: false, entry: null, decision: null };
  }
};

/**
 * Checks if a session has at least one entry of a given type.
 * Works for PLAN, STATUS, STEP_RESULT, or any other entry type.
 *
 * @param {string} sessionId
 * @param {string} entryType
 * @returns {Promise<{ found: boolean, items: object[] }>}
 */
export const checkSessionEntry = async (sessionId, entryType) => {
  if (!sessionId || !entryType) return { found: false, items: [] };

  try {
    const res = await graphqlQuery(getSessionTimeline, { sessionId, entryType });
    const items = (res?.getSessionTimeline?.items || []).filter(
      (entry) => entry?.entryType === entryType
    );
    return { found: items.length > 0, items };
  } catch (err) {
    console.warn('[sessionHelpers] checkSessionEntry error:', err.message);
    return { found: false, items: [] };
  }
};

/**
 * Checks whether any STATUS entry in the session indicates a FAILED state.
 *
 * @param {string} sessionId
 * @returns {Promise<boolean>}
 */
export const checkSessionFailed = async (sessionId) => {
  if (!sessionId) return false;

  try {
    const res = await graphqlQuery(getSessionTimeline, { sessionId, entryType: 'STATUS' });
    const items = (res?.getSessionTimeline?.items || []).filter(
      (entry) => entry?.entryType === 'STATUS'
    );
    return items.some((entry) => {
      const data = parseEntryData(entry?.data);
      return (data?.status || '').toUpperCase() === 'FAILED';
    });
  } catch (err) {
    console.warn('[sessionHelpers] checkSessionFailed error:', err.message);
    return false;
  }
};
